import React from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Form, Icon, Message } from "semantic-ui-react";

import { PinLog, useCan, useGetPinOptionsDropdownOptions, usePermanentLocations } from "components";
import { pinsAttachments } from "components/PinModal/components/AddPinModalForm/utils/pinsAttachments";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES, PIN_TYPES } from "models";
import { NoteAttachments } from "modules/AppointmentDetails/components/Interventions/components";
import { PinToNoteTypeMapping } from "modules/AppointmentDetails/components/Interventions/components/PinCategory/components/PinCategoryForm/usePinCategoryForm";
import { useNoteSuggestions } from "modules/AppointmentDetails/hooks";

import type { PinModelWithHistory } from "components";
import type { Dealer, PIN_STATUSES, Pin, WARRANTY_TYPES } from "models";
import type { NoteAttachment } from "modules/AppointmentDetails/components/Interventions/components";

type AddPinModalFormProps = {
  hasErrors: boolean;
  isSavingPin: boolean;
  pin: PinModelWithHistory;
  attachments: NoteAttachment[];
  isDeleteButtonDisabled: boolean;
  dealer?: Dealer | null;
  readOnly?: boolean;
  onClose: () => void;
  openDeleteConfirmationModal: () => void;
  setAttachments: React.Dispatch<React.SetStateAction<NoteAttachment[]>>;
};

export type AddPinModalFormValues = Pick<Pin, "note" | "support_nr" | "claim_nr" | "ref_nr" | "keep_parts" | "visible_important_items" | "order_status"> & {
  pin_type_id: PIN_TYPES | null;
  pin_status_id: PIN_STATUSES | null;
  warranty_type_id: WARRANTY_TYPES | null;
  note_suggestion?: number;
};

const FormHelper = createFormHelper<AddPinModalFormValues>();

export const AddPinModalForm = ({
  pin,
  dealer,
  hasErrors,
  attachments,
  setAttachments,
  isSavingPin,
  isDeleteButtonDisabled,
  openDeleteConfirmationModal,
  onClose,
  readOnly = false
}: AddPinModalFormProps) => {
  const { t } = useTranslation();

  const canDeletePin = useCan(ACL_ACTIVITIES.Delete, ACL_RESOURCES.Pin);
  const { locations } = usePermanentLocations();
  const { setValue, watch } = FormHelper.useFormContext();

  const [pinTypeId, important, note, orderStatus, keepParts] = watch(["pin_type_id", "visible_important_items", "note", "order_status", "keep_parts"]);
  const { pinOrderOptions, pinStatusOptions, pinTypeOptions, pinWarrantyOptions } = useGetPinOptionsDropdownOptions(dealer);

  const noteSuggestionOptions = useNoteSuggestions(PinToNoteTypeMapping.get(pinTypeId), pin.appointment?.dealer_location_id);
  const pinLogAttachments = pinsAttachments(pin.history);

  const isNoteMissing = important && !note && !pin.note && !keepParts;
  const isDmsWarrantyPin = locations.find(location => location.id === pin.dealer_location_id)?.dms_warranty_pin;
  const showDeleteButton = canDeletePin && !(pin.intervention?.is_warranty_in_dms && isDmsWarrantyPin);

  const selectedPinTypeOption = pinTypeOptions.find(option => option.value === pinTypeId);
  const selectedOrderStatusOption = pinOrderOptions.find(option => option.value === orderStatus);

  const handlePinTypeChange = (pinType: PIN_TYPES | null = null) => {
    setValue("pin_type_id", pinType);

    if (pinType === PIN_TYPES.Remarks) {
      setValue("warranty_type_id", null);
      setValue("pin_status_id", null);
    }
    if (pinType === PIN_TYPES.Recall) setValue("warranty_type_id", null);
  };

  const handleNoteSuggestionChange = (value?: number) => {
    setValue("note", noteSuggestionOptions.find(item => item.value === value)?.text || "");
    setValue("note_suggestion", value);
  };

  const handleKeepPartsChange = (checked: boolean) => {
    setValue("keep_parts", checked);
    if (checked) setValue("visible_important_items", true);
    else if (!note) setValue("visible_important_items", false);
  };

  return (
    <>
      <div className="PinModal_body">
        <div className="PinType">
          <div className="PinModal_content">
            <Form.Group>
              <Form.Field id="pin_type" width={8} className="label-dropdown-container" required error={hasErrors && !pinTypeId}>
                <label>{t("v8_pin_type", "Pin Type")}</label>

                <FormHelper.Field.Dropdown
                  name="pin_type_id"
                  onChange={value => handlePinTypeChange(value)}
                  options={pinTypeOptions}
                  dropdownProps={{
                    placeholder: t("v8_select_pin_type", "Select pin type"),
                    fluid: true,
                    selection: true,
                    disabled: readOnly,
                    trigger: selectedPinTypeOption && (
                      <div>
                        <span>{selectedPinTypeOption.icon}</span>
                        <span className="orderStatusTitle">{selectedPinTypeOption.text}</span>
                      </div>
                    )
                  }}
                />
              </Form.Field>

              <Form.Field id="support_nr" width={8}>
                <label>{t("v8_support", "Support")} #</label>
                <FormHelper.Field.Input name="support_nr" inputProps={{ fluid: true, disabled: readOnly }} />
              </Form.Field>
            </Form.Group>

            <Form.Group>
              <Form.Field id="claim_nr" width={8}>
                <label>{t("v8_claim", "Claim")} #</label>
                <FormHelper.Field.Input name="claim_nr" inputProps={{ fluid: true, disabled: readOnly }} />
              </Form.Field>

              <Form.Field id="ref_nr" width={8}>
                <label>{t("v8_ref", "Ref")} #</label>
                <FormHelper.Field.Input name="ref_nr" inputProps={{ fluid: true, disabled: readOnly }} />
              </Form.Field>
            </Form.Group>

            <Form.Group>
              <Form.Field id="order_status" width={8}>
                <label>{t("v8_order_status", "Order status")}</label>
                <FormHelper.Field.Dropdown
                  name="order_status"
                  options={pinOrderOptions}
                  dropdownProps={{
                    placeholder: t("v8_select_order_status", "Select order status"),
                    fluid: true,
                    selection: true,
                    clearable: true,
                    disabled: readOnly,
                    trigger: selectedOrderStatusOption && (
                      <div>
                        <span>{selectedOrderStatusOption.icon}</span>
                        <span className="orderStatusTitle">{selectedOrderStatusOption.text}</span>
                      </div>
                    )
                  }}
                />
              </Form.Field>

              <Form.Field id="warranty_type" width={8}>
                {pinTypeId === PIN_TYPES.Warranty && (
                  <>
                    <label>{t("v8_warranty_type", "Warranty type")}</label>
                    <FormHelper.Field.Dropdown
                      name="warranty_type_id"
                      options={pinWarrantyOptions}
                      dropdownProps={{ placeholder: t("v8_select_type", "Select type"), fluid: true, selection: true, disabled: readOnly }}
                    />
                  </>
                )}
              </Form.Field>
            </Form.Group>

            <Form.Group>
              <Form.Field id="pin_status" width={10}>
                {pinTypeId !== PIN_TYPES.Remarks && (
                  <>
                    <label>{t("v8_pin_status", "Pin Status")}</label>
                    <FormHelper.Field.Dropdown
                      name="pin_status_id"
                      options={pinStatusOptions}
                      dropdownProps={{ placeholder: t("v8_select_status", "Select status"), fluid: true, selection: true, disabled: readOnly }}
                    />
                  </>
                )}
              </Form.Field>

              <Form.Field id="checkbox_fields" width={6} className="PinModal_checkbox-fields-group">
                <Form.Group className="PinModal_checkbox-fields">
                  <Form.Field id="keep_parts" className="PinModal_checkbox-container-align">
                    <div className="PinModal_checkbox-container">
                      <div>
                        <Icon className="setting" color="grey" />
                      </div>
                      <span>{t("v8_keep_parts", "Keep Parts")}</span>

                      <Checkbox name="keep_parts" checked={keepParts} onChange={(_, { checked }) => handleKeepPartsChange(checked ?? false)} disabled={readOnly} />
                    </div>
                  </Form.Field>

                  <Form.Field id="important" className="PinModal_checkbox-container-align">
                    <div className="PinModal_checkbox-container">
                      <div>
                        <Icon className="circle exclamation" color="red" />
                      </div>
                      <span>{t("v8_important", "Important")}</span>
                      <Checkbox
                        name="visible_important_items"
                        checked={important}
                        onChange={(_, { checked }) => setValue("visible_important_items", checked ?? false)}
                        disabled={readOnly}
                      />
                    </div>
                  </Form.Field>
                </Form.Group>
              </Form.Field>
            </Form.Group>

            {noteSuggestionOptions.length > 0 && (
              <Form.Group>
                <Form.Field id="note_suggestion" width={16}>
                  <label>{t("v8_note", "Note")}</label>
                  <FormHelper.Field.Dropdown
                    name="note_suggestion"
                    options={noteSuggestionOptions}
                    dropdownProps={{
                      selection: true,
                      placeholder: t("v8_choose_answer", "Choose answer"),
                      fluid: true,
                      disabled: readOnly
                    }}
                    onChange={value => handleNoteSuggestionChange(value)}
                  />
                </Form.Field>
              </Form.Group>
            )}

            <Form.Group>
              <Form.Field id="note" width={16}>
                <FormHelper.Field.TextArea name="note" textAreaProps={{ rows: 2, placeholder: t("v8_note", "Note") }} formFieldProps={{ disabled: readOnly }} />
                {(isNoteMissing || hasErrors) && <Message negative content={t("v8_enter_note_to_save", "Enter a note in order to save")} />}
              </Form.Field>
            </Form.Group>

            <Form.Group>
              <Form.Field id="attachments" width={16}>
                <NoteAttachments appointmentId={pin.appointment_id} attachments={attachments} setAttachments={setAttachments} readOnly={readOnly} />
              </Form.Field>
            </Form.Group>

            <Form.Group>
              <Form.Field id="pin_log" width={16}>
                <PinLog
                  readOnly={readOnly}
                  pinLog={pin.history}
                  intervention={pin.intervention}
                  appointment={pin.appointment}
                  pinTypeOptions={pinTypeOptions}
                  pinOrderOptions={pinOrderOptions}
                  pinStatusOptions={pinStatusOptions}
                  pinWarrantyOptions={pinWarrantyOptions}
                  attachments={pinLogAttachments}
                  showTitle={false}
                />
              </Form.Field>
            </Form.Group>
          </div>
        </div>
      </div>

      <div className="PinModal_footer">
        {showDeleteButton && (
          <Form.Button onClick={openDeleteConfirmationModal} color="red" className="PinModal_delete-button" disabled={isDeleteButtonDisabled || readOnly} type="button">
            {t("v8_delete", "Delete")}
            <Icon className="trash" />
          </Form.Button>
        )}

        <div className="PinModal_modal-footer">
          <Form.Button onClick={onClose} basic type="button">
            {t("v8_cancel", "Cancel")}
            <Icon className="xmark" />
          </Form.Button>

          <Form.Button color="green" disabled={isSavingPin || readOnly} loading={isSavingPin} type="submit">
            {pin.id ? t("v8_update", "Update") : t("v8_add_pin", "Add Pin")}
            <Icon className="check" />
          </Form.Button>
        </div>
      </div>
    </>
  );
};
